'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { Input, Button } from '@components/common/Forms';
import { showBuyNeuroModal } from '@components/features/modals/buy-neuro/BuyNeuroModal';
import { useExtendedRouter, useExtendedSearchParams } from '@hooks/next-routing-wrappers';

interface TopUpWalletBarProps {
  isAuth: boolean;
}

export const TopUpWalletBar = ({ isAuth }: TopUpWalletBarProps) => {
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();
  const { searchParamsAsObject } = useExtendedSearchParams();
  const [creditAmount, setCreditAmount] = useState<number>(1000);
  const walletBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchParamsAsObject?.scrollTo === 'wallet-bar' && walletBarRef.current) {
      setTimeout(() => {
        walletBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

        const newParams = new URLSearchParams(window.location.search);
        newParams.delete('scrollTo');
        const newRelativePathQuery = window.location.pathname + (newParams.toString() ? `?${newParams.toString()}` : '');

        // Use router.replace to update URL without refreshing or adding history entry
        router.replace(newRelativePathQuery, { scroll: false });
      }, 100);
    }
  }, [searchParamsAsObject, router]);

  const handleTopUp = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/company/offer?scrollTo=wallet-bar')}`, { withLocale: false });
      return;
    }

    showBuyNeuroModal(creditAmount);
  };

  return (
    <div ref={walletBarRef} className="mb-10 flex items-center gap-4 rounded-md border border-dashed p-4">
      <div className="flex flex-1 items-center gap-14">
        {/* Left Column: Label and Information */}
        <div className="flex flex-col gap-1">
          <label htmlFor="topUpAmount" className="shrink-0 font-medium">
            {t('global.order-neuro')}
          </label>

          <span className="text-sm text-ink-medium">
            {t('neuro.label.order-amount-no-discount')}
          </span>
        </div>

        {/* Input field */}
        <Input
          id="topUpAmount"
          type="number"
          min={1}
          max={200000}
          defaultValue={1000}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || val === null) return;
            const numVal = parseInt(val, 10);
            if (!Number.isNaN(numVal) && numVal >= 1 && numVal <= 200000) {
              setCreditAmount(numVal);
            }
          }}
          className="w-32"
        />
      </div>

      {/* CTA Button */}
      <Button
        onClick={handleTopUp}
        rounding="full"
        color="secondary"
        size="sm"
        className="ml-auto shrink-0"
      >
        {t('global.buy-neuro')}
      </Button>
    </div>
  );
};
